import { hookToAttributeSelector } from '@wix/panda-js-utils';
import { SEAT_SPACING, SEAT_CIRCLE_DIAMETER, SEAT_CIRCLE_RADIUS, ROW_LABEL_WIDTH } from '../../constants';
import { SHAPE } from '../../interfaces';
import { getElement, round, radiansToDegrees360 } from '../../utils/utils';
import { DH } from '../../data-hooks';
import { getFactualElementPosition } from '../../utils/bounding-boxes';
import { createLabelGenerator } from '../../utils/label-generator';
import { getPlaceElementId } from '../../utils/place';
import { getCircleIntersections, getCirclePoint, getDegreesBetweenPoints } from '../../utils/geometry';
const getObjectDegrees = (definition, objectRadius) => {
    const intersections = getCircleIntersections([
        { center: { x: 0, y: 0 }, radius: definition.curveMinRadius },
        { center: { x: 0, y: definition.curveMinRadius }, radius: objectRadius },
    ]);
    const seatDegrees = getDegreesBetweenPoints(intersections[0], { x: 0, y: 0 }, intersections[1]);
    return seatDegrees;
};
export const getSeatDegrees = (definition) => getObjectDegrees(definition, SEAT_CIRCLE_RADIUS);
export const getRowLabelRadius = (definition) => {
    // Some magic calculations based on observations of what looks nice
    const ADJUSTMENT_MAX = 4;
    const ADJUSTMENT_MIN = -1.5;
    let degrees = Math.abs(radiansToDegrees360(definition.curveAngle) % 360);
    if (degrees > 180) {
        degrees = 360 - degrees;
    }
    const diff = ADJUSTMENT_MAX - ADJUSTMENT_MIN;
    const adjustment = (diff / 180) * degrees + ADJUSTMENT_MIN;
    return getObjectDegrees(definition, ROW_LABEL_WIDTH / 2 + adjustment);
};
export const getRowLabelPosition = (definition, straightPosition, left) => {
    if (!definition.curveAngle) {
        return straightPosition;
    }
    const curveCircleCenter = { x: definition.width / 2, y: SEAT_CIRCLE_RADIUS + definition.curveMinRadius };
    const seatDegrees = getSeatDegrees(definition);
    const rowDegrees = Math.max(seatDegrees, radiansToDegrees360(definition.curveAngle));
    const labelAngleDegrees = getRowLabelRadius(definition);
    const labelPositionDegrees = left
        ? round(-(rowDegrees / 2) - labelAngleDegrees)
        : round(rowDegrees / 2 + labelAngleDegrees);
    const centerPosition = getCirclePoint(labelPositionDegrees, curveCircleCenter, definition.curveMinRadius);
    const position = {
        x: centerPosition.x - 10,
        y: centerPosition.y - SEAT_CIRCLE_RADIUS,
    };
    return position;
};
const HORIZONTAL_LABEL_PADDING = 6;
export const getLeftRowLabelPosition = (definition) => getRowLabelPosition(definition, { x: -(ROW_LABEL_WIDTH + HORIZONTAL_LABEL_PADDING), y: 0 }, true);
export const getRightRowLabelPosition = (definition) => getRowLabelPosition(definition, { x: definition.width + HORIZONTAL_LABEL_PADDING, y: 0 }, false);
export const getSeatsAmount = (size, spacing = SEAT_SPACING) => {
    const reservedSeatSpace = spacing + SEAT_CIRCLE_DIAMETER;
    const amount = size < SEAT_CIRCLE_DIAMETER ? 0 : Math.floor(size / reservedSeatSpace) + 1;
    return amount;
};
export const getSeatsLength = (seats, spacing = SEAT_SPACING) => {
    return SEAT_CIRCLE_DIAMETER * seats + spacing * (seats - 1);
};
export const getRowContainerInfo = (definition) => {
    if (definition.curveAngle) {
        const curveCircleCenter = { x: definition.width / 2, y: SEAT_CIRCLE_RADIUS + definition.curveMinRadius };
        const seatDegrees = getSeatDegrees(definition);
        const rowDegrees = Math.max(seatDegrees, radiansToDegrees360(definition.curveAngle) - seatDegrees);
        const seatCenterSpacing = rowDegrees / (definition.capacity - 1);
        const lastSeatDegrees = (definition.capacity - 1) * seatCenterSpacing - rowDegrees / 2;
        const edgeSeatDegrees = Math.min(90, lastSeatDegrees);
        const edgeSeatCenter = getCirclePoint(edgeSeatDegrees, curveCircleCenter, definition.curveMinRadius);
        const lastSeatCenter = getCirclePoint(lastSeatDegrees, curveCircleCenter, definition.curveMinRadius);
        const edgeSeatRight = edgeSeatCenter.x + SEAT_CIRCLE_RADIUS;
        const lastSeatBottom = lastSeatCenter.y + SEAT_CIRCLE_RADIUS;
        const deltaWidth = (edgeSeatRight - definition.width) * 2;
        const width = definition.width + deltaWidth;
        const height = lastSeatBottom;
        const relativeX = -(edgeSeatRight - definition.width);
        const relativeY = 0;
        return {
            width,
            height,
            relativeX,
            relativeY,
        };
    }
    else {
        return {
            relativeX: 0,
            relativeY: 0,
            height: SEAT_CIRCLE_DIAMETER,
            width: definition.width,
        };
    }
};
export const getRows = ({ definition, absolutePosition = false, shapeContainer, actionContainer, svgViewBox, }) => {
    const { width, startAt, labelPosition, reverseDirection, sectorLabel, sequence, spacing = SEAT_SPACING, spacingBetweenRows: rowSpacing = SEAT_SPACING, shapeBorderColor, shapeColor, shapeTextColor, rowsSequence, rowsStartAt, rowsReverseDirection, groupId, } = definition;
    const verticalReservedSeatSpace = rowSpacing + SEAT_CIRCLE_DIAMETER;
    const rowsAmount = getSeatsAmount(definition.height, definition.spacingBetweenRows);
    const rows = [];
    let rowPositions = [];
    if (absolutePosition) {
        if (shapeContainer) {
            rowPositions = getActualRowPositions(definition, shapeContainer, svgViewBox) ?? [];
        }
        if (!rowPositions.length && actionContainer) {
            rowPositions = getActualRowPositions(definition, actionContainer, svgViewBox) ?? [];
        }
    }
    const rowLabelGenerator = createLabelGenerator({
        sequence: rowsSequence,
        startAt: rowsStartAt,
        reverseDirection: rowsReverseDirection,
        capacity: rowsAmount,
    });
    const rowPlaceMap = {};
    for (const place of definition.places ?? []) {
        const shapeId = getPlaceElementId(place);
        rowPlaceMap[shapeId] = rowPlaceMap[shapeId] ?? [];
        rowPlaceMap[shapeId].push(place);
    }
    definition.rows.forEach((row, rowIndex) => {
        const capacity = getSeatsAmount(row.overrides.width ?? width, row.overrides.spacing ?? spacing);
        const defaultRowLabel = rowLabelGenerator.getLabelByIndex(rowIndex);
        const rowDefinition = {
            id: row.id,
            type: SHAPE.ROW,
            angle: 0,
            x: row.overrides?.relativeX ?? 0,
            y: rowIndex * verticalReservedSeatSpace,
            width: row.overrides.width ?? width,
            height: SEAT_CIRCLE_DIAMETER,
            startAt: row.overrides?.startAt ?? startAt,
            label: row.overrides?.label ?? defaultRowLabel,
            labelPosition: row.overrides?.labelPosition ?? labelPosition,
            reverseDirection: row.overrides?.reverseDirection ?? reverseDirection,
            capacity,
            sectorLabel,
            sequence: row.overrides?.sequence ?? sequence,
            spacing: row.overrides?.spacing ?? spacing,
            shapeBorderColor: row.shapeBorderColor ?? shapeBorderColor,
            shapeColor: row.shapeColor ?? shapeColor,
            shapeTextColor: row.shapeTextColor ?? shapeTextColor,
            groupId,
            places: rowPlaceMap[row.id] ?? [],
            categoryId: definition?.categoryId,
            placeOverrides: row.placeOverrides ?? [],
            curveAngle: 0,
            curveMinRadius: 0, // 300, // FIXME: Test data - Not finished!
        };
        if (absolutePosition) {
            rowDefinition.x = rowPositions[rows.length].x - definition.x;
            rowDefinition.y = rowPositions[rows.length].y - definition.y;
            rowDefinition.angle = definition.angle;
        }
        rows.push(rowDefinition);
    });
    return rows;
};
const getActualRowPositions = (definition, shapeContainer, svgViewBox) => {
    if (!shapeContainer && !shapeContainer.parentElement) {
        throw new Error('Cannot calculate absolute position of rows without having a reference to main SVG container');
    }
    const multiRowElement = getElement(definition.id, shapeContainer.parentElement, undefined);
    if (!multiRowElement) {
        return null;
    }
    const rowNodeList = multiRowElement.querySelectorAll(hookToAttributeSelector(DH.MULTI_ROW_ROW));
    const rowRootElements = [];
    rowNodeList.forEach(node => rowRootElements.push(node));
    const rowSeatWrapperElements = rowRootElements.map(root => {
        const seats = root.querySelector(hookToAttributeSelector(DH.MULTI_ROW_ROW_SEATS));
        return seats.querySelector('[data-measure-during-apply]') || seats;
    });
    const positions = rowSeatWrapperElements.map((el, index) => getFactualElementPosition({
        element: el,
        definition: { ...definition, height: SEAT_CIRCLE_DIAMETER, width: definition.rows[index].overrides.width },
        container: shapeContainer,
        svgViewBox,
    }));
    return positions;
};
//# sourceMappingURL=utils.js.map